import {
    defaultFilterValues,
    FilterInput,
    FilterItem,
    FilterItemExtended,
    FilterOutput,
    FiltersWithChecked,
    FilterValues,
    MenuPartItemOutput
} from "@vms/SelectFilters/SelectFiltersTypes";
import { Segment, segmentList } from "@models/Segment";
import { SLICES, SlicesEnum } from "@models/Byo";

const addCheckedFlag = <T extends FilterItemExtended>(
    allItems?: T[] | null,
    selectedIds?: number[] | null,
    defaultChecked: boolean = true
): (T & { isChecked: boolean })[] => {
    return (allItems || []).map((item) => ({
        ...item,
        isChecked: selectedIds === undefined || selectedIds === null
            ? defaultChecked
            : selectedIds.includes(item.id!)
    }));
};
const extractIds = (items?: FilterItemExtended[] | null): number[] => {
    return (items ?? []).map(item => item.id!).filter(id => id !== undefined);
};
const getMenuItemTypes = (
    allMenuParts: MenuPartItemOutput[] | null | undefined,
    selectedMenuParts: MenuPartItemOutput[] | null | undefined,
    menuPartName: string,
    defaultChecked: boolean = true
): FiltersWithChecked<FilterItem> => {
    if (!allMenuParts) return [];
    const allMenuPart = allMenuParts.find((m) => m.name?.toLowerCase() === menuPartName);

    const ITEM_TYPES_SLICE = SLICES.find(s => s.id === SlicesEnum.ItemTypes);
    const { columnsOrder = [] } = ITEM_TYPES_SLICE || {};

    allMenuPart?.menuItemTypes?.sort((a, b) => {
        const aScore = columnsOrder.findIndex(c => c === a.id);
        const bScore = columnsOrder.findIndex(c => c === b.id);
        return aScore - bScore;
    });

    const selectedMenuPart = selectedMenuParts?.find((m) => m.name?.toLowerCase() === menuPartName);
    const selectedIds = selectedMenuParts ? extractIds(selectedMenuPart?.menuItemTypes) : undefined;
    return addCheckedFlag(allMenuPart?.menuItemTypes, selectedIds, defaultChecked);
};

export function mapFiltersToClient(
    activeRestaurantListId: number,
    filtersData?: FilterOutput,
    selectedFiltersData?: FilterInput,
    defaultChecked: boolean = true
): FilterValues {
    if (!filtersData) return defaultFilterValues;

    const {
        restaurantFilters: allRestaurantFilters,
        menuPartFilterItems: allMenuParts,
        otherFilters: allOtherFilters
    } = filtersData;
    const {
        restaurantFilters: selectedRestaurantFilters,
        menuPartFilterItems: selectedMenuParts,
        otherFilters: selectedOtherFilters
    } = selectedFiltersData || {};

    const selectedSegmentIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.segments) : undefined;
    const selectedRegionIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.regions) : undefined;
    const selectedCuisineIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.cuisines) : undefined;
    const selectedHostedLocationIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.hostedLocations) : undefined;
    const selectedDayPartIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.dayParts) : undefined;

    const catering = selectedOtherFilters ? selectedOtherFilters.catering : false;

    return {
        activeRestaurantListId,
        venues: {
            segments: addCheckedFlag(allRestaurantFilters?.segments, selectedSegmentIds, defaultChecked)
                .sort((a, b) => {
                    return segmentList.indexOf(a.name!.toLowerCase() as Segment) - segmentList.indexOf(b.name!.toLowerCase() as Segment);
                }),
            regions: addCheckedFlag(allRestaurantFilters?.regions, selectedRegionIds, defaultChecked),
            cuisines: addCheckedFlag(allRestaurantFilters?.cuisines, selectedCuisineIds, defaultChecked),
            hostedLocations: addCheckedFlag(allRestaurantFilters?.hostedLocations, selectedHostedLocationIds, defaultChecked),
            restaurants: selectedOtherFilters?.restaurants || [],
            minUnits: selectedOtherFilters?.minUnits ?? allOtherFilters?.minUnits ?? 1,
            maxUnits: selectedOtherFilters?.maxUnits ?? allOtherFilters?.maxUnits ?? null,
        },
        menuItems: {
            appetizers: getMenuItemTypes(allMenuParts, selectedMenuParts, 'appetizer', defaultChecked),
            entrees: getMenuItemTypes(allMenuParts, selectedMenuParts, 'entree', defaultChecked),
            sides: getMenuItemTypes(allMenuParts, selectedMenuParts, 'side', defaultChecked),
            desserts: getMenuItemTypes(allMenuParts, selectedMenuParts, 'dessert', defaultChecked),
            beverages: getMenuItemTypes(allMenuParts, selectedMenuParts, 'beverage', defaultChecked),
            words: selectedOtherFilters?.customWords || [],
            minPrice: selectedOtherFilters?.minPrice ?? allOtherFilters?.minPrice ?? null,
            maxPrice: selectedOtherFilters?.maxPrice ?? allOtherFilters?.maxPrice ?? null,
        },
        attributes: {
            dayParts: addCheckedFlag(allRestaurantFilters?.dayParts, selectedDayPartIds, defaultChecked),
            ethnicSpeciality: [],
            healthyTerms: selectedOtherFilters?.healthyDining ?? allOtherFilters?.healthyDining ?? false,
            smallPlates: selectedOtherFilters?.smallPlates ?? allOtherFilters?.smallPlates ?? false,
            kidMenus: selectedOtherFilters?.kidMenus ?? allOtherFilters?.kidMenus ?? false,
            glutenFree: selectedOtherFilters?.glutenFree ?? allOtherFilters?.glutenFree ?? false,
            plantBased: selectedOtherFilters?.plantBased ?? allOtherFilters?.plantBased ?? false,
            catering: typeof catering === 'boolean'
                ? catering
                : null,
            tags: selectedOtherFilters?.tags ?? [],
        },
    };
}